import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import ReportWidget from './ReportWidget';
import ReportWidgetEdit from './ReportWidgetEdit';
import CentralCalls from '../../centralCalls';

const GRID_COLUMNS = 8;

function getRequiredRows(widgets) {
	if (!Array.isArray(widgets) || widgets.length === 0) {
		return 1;
	}

	return widgets.reduce((maxRow, widget) => {
		const y = Number(widget?.yCoordinate) || 1;
		const height = Number(widget?.height) || 1;
		const endRow = y + height - 1;
		return Math.max(maxRow, endRow);
	}, 1);
}

function parsePageJson(rawPageJson) {
	if (!rawPageJson) {
		return null;
	}

	if (typeof rawPageJson === 'object') {
		console.log('ALREADY OBJECT');
		return rawPageJson;
	}

	if (typeof rawPageJson !== 'string') {
		console.log('STRING  type')
		return null;
	}

	const trimmed = rawPageJson.trim();
	if (!trimmed) {
		return null;
	}

	const parseJsonPayload = (input) => {
		const parsed = JSON.parse(input);
		if (typeof parsed === 'string') {
			return JSON.parse(parsed);
		}
		return parsed;
	};

	try {
		return parseJsonPayload(trimmed);
	} catch {
		const normalized = trimmed
			.replace(/([{,]\s*)([A-Za-z_$][A-Za-z0-9_$]*)(\s*:)/g, '$1"$2"$3')
			.replace(/'([^'\\]*(?:\\.[^'\\]*)*)'/g, (_, content) => `"${String(content).replace(/"/g, '\\"')}"`)
			.replace(/,(\s*[}\]])/g, '$1');

		return parseJsonPayload(normalized);
	}
}

function ReportGrid({ isEditMode = false, reportPageOid = null }) {
	const hostRef = useRef(null);
	const [hostWidth, setHostWidth] = useState(0);
	const [widgets, setWidgets] = useState([]);
	const [pageModel, setPageModel] = useState(null);
	const [loadError, setLoadError] = useState('');
	const [editingWidgetIndex, setEditingWidgetIndex] = useState(null);
	const [draftWidget, setDraftWidget] = useState(null);
	const [deleteWidgetIndex, setDeleteWidgetIndex] = useState(null);
	const [creationStartCell, setCreationStartCell] = useState(null);
	const [hoverCell, setHoverCell] = useState(null);
	const [resizeState, setResizeState] = useState(null);
	const [moveState, setMoveState] = useState(null);
	const [isSaving, setIsSaving] = useState(false);
	const [filterValues, setFilterValues] = useState({});
	const [editGridRows, setEditGridRows] = useState(null);
	const rows = useMemo(() => getRequiredRows(widgets), [widgets]);
	const gridRows = useMemo(() => {
		if (!isEditMode) {
			return rows;
		}

		const baselineRows = Math.max(rows, 8);
		if (typeof editGridRows === 'number' && Number.isFinite(editGridRows)) {
			return Math.max(baselineRows, Math.floor(editGridRows));
		}

		return baselineRows;
	}, [isEditMode, rows, editGridRows]);

	useEffect(() => {
		let isMounted = true;

		const loadWidgetsFromReportPage = async () => {
			if (!reportPageOid) {
				setWidgets([]);
				setPageModel(null);
				setFilterValues({});
				setEditGridRows(null);
				return;
			}

			try {
				const rows = await CentralCalls.tableSelect('reporting_page', { oid: reportPageOid });
				const row = Array.isArray(rows) && rows.length > 0 ? rows[0] : null;
				const rawPageJson = row?.page_json ?? row?.PageJson;
				const parsedPage = parsePageJson(rawPageJson);
				if (!isMounted) {
					return;
				}

				setPageModel(parsedPage && typeof parsedPage === 'object' ? parsedPage : {});
				setWidgets(Array.isArray(parsedPage?.widgets) ? parsedPage.widgets : []);
				setEditGridRows(null);
				setFilterValues({});
				setLoadError('');
			} catch (error) {
				if (isMounted) {
					setWidgets([]);
					setPageModel(null);
					setFilterValues({});
					setEditGridRows(null);
					setLoadError('Failed to load report page.');
				}
				console.error('Failed to load report page widgets', error);
			}
		};

		loadWidgetsFromReportPage();

		return () => {
			isMounted = false;
		};
	}, [reportPageOid]);

	const handleFilterValueChange = useCallback((filterKey, nextValue) => {
		if (typeof filterKey !== 'string' || !filterKey.trim()) {
			return;
		}

		const key = filterKey.trim();
		setFilterValues((prev) => {
			const safePrev = prev && typeof prev === 'object' ? prev : {};

			if (nextValue === undefined || nextValue === null || nextValue === '') {
				if (!(key in safePrev)) {
					return safePrev;
				}
				const next = { ...safePrev };
				delete next[key];
				return next;
			}

			const normalizedValue = String(nextValue);
			if (safePrev[key] === normalizedValue) {
				return safePrev;
			}

			return {
				...safePrev,
				[key]: normalizedValue,
			};
		});
	}, []);

	useEffect(() => {
		if (!isEditMode) {
			setEditingWidgetIndex(null);
			setDraftWidget(null);
			setCreationStartCell(null);
			setHoverCell(null);
			setMoveState(null);
			setEditGridRows(null);
		}
	}, [isEditMode]);

	const isCellOccupied = (x, y, list = widgets) => {
		return list.some((widget) => {
			const left = Number(widget?.xCoordinate) || 1;
			const top = Number(widget?.yCoordinate) || 1;
			const width = Number(widget?.width) || 1;
			const height = Number(widget?.height) || 1;
			return x >= left && x <= left + width - 1 && y >= top && y <= top + height - 1;
		});
	};

	const hasRectOverlap = useCallback((rect, list = widgets, ignoreIndex = null) => {
		return list.some((widget, index) => {
			if (ignoreIndex !== null && index === ignoreIndex) {
				return false;
			}
			const left = Number(widget?.xCoordinate) || 1;
			const top = Number(widget?.yCoordinate) || 1;
			const width = Number(widget?.width) || 1;
			const height = Number(widget?.height) || 1;
			const right = left + width - 1;
			const bottom = top + height - 1;

			const noHorizontalOverlap = rect.x + rect.width - 1 < left || rect.x > right;
			const noVerticalOverlap = rect.y + rect.height - 1 < top || rect.y > bottom;
			return !(noHorizontalOverlap || noVerticalOverlap);
		});
	}, [widgets]);

	const getWidgetRect = (widget) => ({
		x: Number(widget?.xCoordinate) || 1,
		y: Number(widget?.yCoordinate) || 1,
		width: Number(widget?.width) || 1,
		height: Number(widget?.height) || 1,
	});

	const isRectWithinGrid = useCallback((rect) => {
		if (!rect) {
			return false;
		}
		if (rect.x < 1 || rect.y < 1) {
			return false;
		}
		if (rect.width < 1 || rect.height < 1) {
			return false;
		}
		if (rect.x + rect.width - 1 > GRID_COLUMNS) {
			return false;
		}
		if (rect.y + rect.height - 1 > gridRows) {
			return false;
		}
		return true;
	}, [gridRows]);

	const buildResizedRect = (originRect, side, deltaCols, deltaRows) => {
		const next = { ...originRect };

		if (side === 'right') {
			next.width = Math.max(1, originRect.width + deltaCols);
		}
		if (side === 'left') {
			const nextX = originRect.x + deltaCols;
			const rightEdge = originRect.x + originRect.width - 1;
			next.x = Math.min(rightEdge, Math.max(1, nextX));
			next.width = rightEdge - next.x + 1;
		}
		if (side === 'bottom') {
			next.height = Math.max(1, originRect.height + deltaRows);
		}
		if (side === 'top') {
			const nextY = originRect.y + deltaRows;
			const bottomEdge = originRect.y + originRect.height - 1;
			next.y = Math.min(bottomEdge, Math.max(1, nextY));
			next.height = bottomEdge - next.y + 1;
		}

		return next;
	};

	const makeRectFromCells = (start, end) => {
		if (!start || !end) {
			return null;
		}
		if (end.x < start.x || end.y < start.y) {
			return null;
		}

		return {
			x: start.x,
			y: start.y,
			width: end.x - start.x + 1,
			height: end.y - start.y + 1,
		};
	};

	const canUseEndCell = (endCell) => {
		const rect = makeRectFromCells(creationStartCell, endCell);
		if (!rect) {
			return false;
		}
		return !hasRectOverlap(rect, widgets);
	};

	const persistPageModel = useCallback(async (nextPageModel) => {
		if (!reportPageOid) {
			return;
		}
		await CentralCalls.upsertData(
			{
				oid: reportPageOid,
				page_json: JSON.stringify(nextPageModel),
			},
			'reporting_page',
			'update'
		);
	}, [reportPageOid]);

	const handleAddRow = useCallback(async () => {
		if (!isEditMode || isSaving) {
			return;
		}

		setEditGridRows((prev) => {
			const baselineRows = Math.max(rows, 8);
			const currentRows = typeof prev === 'number' && Number.isFinite(prev) ? Math.floor(prev) : baselineRows;
			return currentRows + 1;
		});
	}, [isEditMode, isSaving, rows]);

	const handleWidgetPatch = useCallback(async (index, patch) => {
		if (index < 0 || index >= widgets.length || !patch || typeof patch !== 'object') {
			return;
		}

		const currentWidget = widgets[index] || {};
		const patchData = patch?.data && typeof patch.data === 'object' && !Array.isArray(patch.data) ? patch.data : null;
		const nextWidget = {
			...currentWidget,
			...patch,
			data: patchData
				? {
					...(currentWidget?.data && typeof currentWidget.data === 'object' ? currentWidget.data : {}),
					...patchData,
				}
				: currentWidget?.data,
		};

		const nextWidgets = widgets.map((widget, widgetIndex) => (widgetIndex === index ? nextWidget : widget));
		const nextPageModel = {
			...(pageModel && typeof pageModel === 'object' ? pageModel : {}),
			widgets: nextWidgets,
		};

		try {
			setIsSaving(true);
			await persistPageModel(nextPageModel);
			setWidgets(nextWidgets);
			setPageModel(nextPageModel);
			setLoadError('');
		} catch (error) {
			setLoadError('Failed to update widget.');
			console.error('Failed to patch widget', error);
			throw error;
		} finally {
			setIsSaving(false);
		}
	}, [widgets, pageModel, persistPageModel]);

	const getWidgetName = (widget) => {
		return widget?.headerLabel || widget?.data?.headerLabel || widget?.type || widget?.id || 'Widget';
	};

	const openEditor = (index) => {
		setCreationStartCell(null);
		setHoverCell(null);
		setEditingWidgetIndex(index);
		setDraftWidget(widgets[index] || null);
	};

	const coordinateSize = useMemo(() => {
		if (!hostWidth) {
			return 0;
		}
		return Math.max(1, hostWidth / GRID_COLUMNS);
	}, [hostWidth]);

	const startResize = (event, index, side) => {
		event.preventDefault();
		event.stopPropagation();
		if (!isEditMode || isSaving || editingWidgetIndex !== null || moveState) {
			return;
		}

		setCreationStartCell(null);
		setHoverCell(null);

		const originWidget = widgets[index];
		if (!originWidget) {
			return;
		}

		setResizeState({
			index,
			side,
			startX: event.clientX,
			startY: event.clientY,
			originRect: getWidgetRect(originWidget),
			baseWidgets: widgets,
			currentRect: getWidgetRect(originWidget),
		});
	};

	const startMove = (event, index) => {
		event.preventDefault();
		event.stopPropagation();
		if (!isEditMode || isSaving || editingWidgetIndex !== null || resizeState) {
			return;
		}

		setCreationStartCell(null);
		setHoverCell(null);

		const originWidget = widgets[index];
		if (!originWidget) {
			return;
		}

		setMoveState({
			index,
			startX: event.clientX,
			startY: event.clientY,
			originRect: getWidgetRect(originWidget),
			baseWidgets: widgets,
			currentRect: getWidgetRect(originWidget),
		});
	};

	useEffect(() => {
		if (!resizeState) {
			return undefined;
		}

		const handleMouseMove = (event) => {
			const step = Math.max(1, coordinateSize);
			const deltaCols = Math.round((event.clientX - resizeState.startX) / step);
			const deltaRows = Math.round((event.clientY - resizeState.startY) / step);
			const candidateRect = buildResizedRect(resizeState.originRect, resizeState.side, deltaCols, deltaRows);

			if (!isRectWithinGrid(candidateRect)) {
				return;
			}
			if (hasRectOverlap(candidateRect, resizeState.baseWidgets, resizeState.index)) {
				return;
			}

			const nextWidgets = resizeState.baseWidgets.map((widget, idx) => {
				if (idx !== resizeState.index) {
					return widget;
				}
				return {
					...widget,
					xCoordinate: candidateRect.x,
					yCoordinate: candidateRect.y,
					width: candidateRect.width,
					height: candidateRect.height,
				};
			});

			setWidgets(nextWidgets);
			setResizeState((prev) => (prev ? { ...prev, currentRect: candidateRect } : prev));
		};

		const handleMouseUp = async () => {
			const finalState = resizeState;
			setResizeState(null);

			if (!finalState || !finalState.currentRect) {
				return;
			}

			const nextWidgets = finalState.baseWidgets.map((widget, idx) => {
				if (idx !== finalState.index) {
					return widget;
				}
				return {
					...widget,
					xCoordinate: finalState.currentRect.x,
					yCoordinate: finalState.currentRect.y,
					width: finalState.currentRect.width,
					height: finalState.currentRect.height,
				};
			});

			const nextPageModel = {
				...(pageModel && typeof pageModel === 'object' ? pageModel : {}),
				widgets: nextWidgets,
			};

			try {
				setIsSaving(true);
				await persistPageModel(nextPageModel);
				setWidgets(nextWidgets);
				setPageModel(nextPageModel);
				setLoadError('');
			} catch (error) {
				setLoadError('Failed to resize widget.');
				console.error('Failed to resize widget', error);
			} finally {
				setIsSaving(false);
			}
		};

		window.addEventListener('mousemove', handleMouseMove);
		window.addEventListener('mouseup', handleMouseUp);
		return () => {
			window.removeEventListener('mousemove', handleMouseMove);
			window.removeEventListener('mouseup', handleMouseUp);
		};
	}, [resizeState, coordinateSize, pageModel, hasRectOverlap, isRectWithinGrid, persistPageModel]);

	useEffect(() => {
		if (!moveState) {
			return undefined;
		}

		const handleMouseMove = (event) => {
			const step = Math.max(1, coordinateSize);
			const deltaCols = Math.round((event.clientX - moveState.startX) / step);
			const deltaRows = Math.round((event.clientY - moveState.startY) / step);
			const candidateRect = {
				...moveState.originRect,
				x: moveState.originRect.x + deltaCols,
				y: moveState.originRect.y + deltaRows,
			};

			if (!isRectWithinGrid(candidateRect)) {
				return;
			}
			if (hasRectOverlap(candidateRect, moveState.baseWidgets, moveState.index)) {
				return;
			}

			const nextWidgets = moveState.baseWidgets.map((widget, idx) => {
				if (idx !== moveState.index) {
					return widget;
				}
				return {
					...widget,
					xCoordinate: candidateRect.x,
					yCoordinate: candidateRect.y,
				};
			});

			setWidgets(nextWidgets);
			setMoveState((prev) => (prev ? { ...prev, currentRect: candidateRect } : prev));
		};

		const handleMouseUp = async () => {
			const finalState = moveState;
			setMoveState(null);

			if (!finalState || !finalState.currentRect) {
				return;
			}

			const nextWidgets = finalState.baseWidgets.map((widget, idx) => {
				if (idx !== finalState.index) {
					return widget;
				}
				return {
					...widget,
					xCoordinate: finalState.currentRect.x,
					yCoordinate: finalState.currentRect.y,
				};
			});

			const nextPageModel = {
				...(pageModel && typeof pageModel === 'object' ? pageModel : {}),
				widgets: nextWidgets,
			};

			try {
				setIsSaving(true);
				await persistPageModel(nextPageModel);
				setWidgets(nextWidgets);
				setPageModel(nextPageModel);
				setLoadError('');
			} catch (error) {
				setLoadError('Failed to move widget.');
				console.error('Failed to move widget', error);
			} finally {
				setIsSaving(false);
			}
		};

		window.addEventListener('mousemove', handleMouseMove);
		window.addEventListener('mouseup', handleMouseUp);
		return () => {
			window.removeEventListener('mousemove', handleMouseMove);
			window.removeEventListener('mouseup', handleMouseUp);
		};
	}, [moveState, coordinateSize, pageModel, hasRectOverlap, isRectWithinGrid, persistPageModel]);

	const closeEditor = () => {
		if (isSaving) {
			return;
		}
		setEditingWidgetIndex(null);
		setDraftWidget(null);
	};

	const requestDeleteWidget = (index) => {
		if (isSaving) {
			return;
		}
		setDeleteWidgetIndex(index);
	};

	const cancelDeleteWidget = () => {
		if (isSaving) {
			return;
		}
		setDeleteWidgetIndex(null);
	};

	const confirmDeleteWidget = async () => {
		if (deleteWidgetIndex === null || deleteWidgetIndex < 0 || deleteWidgetIndex >= widgets.length) {
			setDeleteWidgetIndex(null);
			return;
		}

		const nextWidgets = widgets.filter((_, index) => index !== deleteWidgetIndex);
		const nextPageModel = {
			...(pageModel && typeof pageModel === 'object' ? pageModel : {}),
			widgets: nextWidgets,
		};

		try {
			setIsSaving(true);
			await persistPageModel(nextPageModel);
			setWidgets(nextWidgets);
			setPageModel(nextPageModel);
			setLoadError('');
			if (editingWidgetIndex === deleteWidgetIndex) {
				closeEditor();
			}
			setDeleteWidgetIndex(null);
		} catch (error) {
			setLoadError('Failed to delete widget.');
			console.error('Failed to delete widget', error);
		} finally {
			setIsSaving(false);
		}
	};

	const handleSaveWidget = async () => {
		if (editingWidgetIndex === null || !draftWidget) {
			return;
		}

		const nextWidgets = [...widgets];
		nextWidgets[editingWidgetIndex] = draftWidget;
		const nextPageModel = {
			...(pageModel && typeof pageModel === 'object' ? pageModel : {}),
			widgets: nextWidgets,
		};

		try {
			setIsSaving(true);
			await persistPageModel(nextPageModel);

			setWidgets(nextWidgets);
			setPageModel(nextPageModel);
			setLoadError('');
			closeEditor();
		} catch (error) {
			setLoadError('Failed to save report page.');
			console.error('Failed to save report page', error);
		} finally {
			setIsSaving(false);
		}
	};

	const handleCellClick = async (cell) => {
		if (!isEditMode || editingWidgetIndex !== null || resizeState || moveState) {
			return;
		}

		if (!creationStartCell) {
			if (isCellOccupied(cell.x, cell.y, widgets)) {
				return;
			}
			setCreationStartCell(cell);
			setHoverCell(cell);
			setLoadError('');
			return;
		}

		const rect = makeRectFromCells(creationStartCell, cell);
		if (!rect || hasRectOverlap(rect, widgets)) {
			setLoadError('Invalid widget area. End cell must be to the right/down and not overlap existing widgets.');
			return;
		}

		const newWidget = {
			id: `widget-${Date.now()}-${Math.floor(Math.random() * 1000)}`,
			type: 'single',
			xCoordinate: rect.x,
			yCoordinate: rect.y,
			width: rect.width,
			height: rect.height,
			procedureName: '',
			data: {
				headerLabel: 'New Widget',
				subHeaderLabel: '',
			},
		};

		const nextWidgets = [...widgets, newWidget];
		const nextPageModel = {
			...(pageModel && typeof pageModel === 'object' ? pageModel : {}),
			widgets: nextWidgets,
		};

		try {
			setIsSaving(true);
			await persistPageModel(nextPageModel);
			setWidgets(nextWidgets);
			setPageModel(nextPageModel);
			setCreationStartCell(null);
			setHoverCell(null);
			setLoadError('');
		} catch (error) {
			setLoadError('Failed to create widget.');
			console.error('Failed to create widget', error);
		} finally {
			setIsSaving(false);
		}
	};

	useEffect(() => {
		if (!hostRef.current) {
			return undefined;
		}

		const node = hostRef.current;
		const resizeObserver = new ResizeObserver((entries) => {
			const entry = entries[0];
			if (!entry?.contentRect) {
				return;
			}

			setHostWidth(entry.contentRect.width);
		});

		resizeObserver.observe(node);

		return () => {
			resizeObserver.disconnect();
		};
	}, []);

	const gridHeight = coordinateSize * gridRows;

	const previewRect =
		creationStartCell && hoverCell && canUseEndCell(hoverCell)
			? makeRectFromCells(creationStartCell, hoverCell)
			: null;

	const allGridCells = useMemo(() => {
		const cells = [];
		for (let y = 1; y <= gridRows; y += 1) {
			for (let x = 1; x <= GRID_COLUMNS; x += 1) {
				cells.push({ x, y, id: `${x}-${y}` });
			}
		}
		return cells;
	}, [gridRows]);

	return (
		<div className="m-0 h-full min-h-full min-w-0 flex-1 bg-white p-0">
			{isEditMode ? (
				<div className="flex items-center justify-end px-3 py-2">
					<button
						type="button"
						onClick={handleAddRow}
						disabled={isSaving}
						className="rounded-md border border-slate-300 px-3 py-1.5 text-xs font-medium text-slate-700 transition hover:bg-slate-100 disabled:cursor-not-allowed disabled:opacity-60"
					>
						+ Add Row
					</button>
				</div>
			) : null}
			{loadError ? <div className="px-3 py-2 text-sm text-red-700">{loadError}</div> : null}
			<section ref={hostRef} className="h-full min-h-[520px] w-full overflow-auto" aria-label="Report Grid">
				<div className="w-full" style={{ minHeight: '320px', height: gridHeight > 0 ? `${gridHeight}px` : 'auto' }}>
					<div
						className="relative grid w-full"
						style={{
							gridTemplateColumns: `repeat(${GRID_COLUMNS}, minmax(0, 1fr))`,
							gridTemplateRows:
								coordinateSize > 0
									? `repeat(${gridRows}, ${coordinateSize}px)`
									: `repeat(${gridRows}, minmax(48px, auto))`,
						}}
					>
						{isEditMode
							? allGridCells
									.filter((cell) => !isCellOccupied(cell.x, cell.y, widgets))
									.map((cell) => {
										const isStartCell = creationStartCell?.x === cell.x && creationStartCell?.y === cell.y;
										const isValidEnd = !!creationStartCell && canUseEndCell(cell);
										return (
											<button
												type="button"
												key={`cell-${cell.id}`}
												onClick={() => handleCellClick(cell)}
												onMouseEnter={() => {
													if (creationStartCell) {
														setHoverCell(cell);
													}
												}}
												className={`z-[1] m-[2px] rounded-md border border-dashed text-[0px] transition ${
													isStartCell
														? 'border-sky-500 bg-sky-100/80'
														: isValidEnd
														? 'border-sky-300 bg-sky-50/60 hover:bg-sky-100/70'
														: 'border-slate-300 bg-slate-100/60 hover:bg-slate-200/70'
												}`}
												style={{
													gridColumn: `${cell.x} / span 1`,
													gridRow: `${cell.y} / span 1`,
												}}
												aria-label={`Grid cell ${cell.x},${cell.y}`}
												title={`Grid cell ${cell.x},${cell.y}`}
											/>
										);
									})
							: null}

						{isEditMode && previewRect ? (
							<div
								className="pointer-events-none z-[2] m-[2px] rounded-md border-2 border-sky-500 bg-sky-300/30"
								style={{
									gridColumn: `${previewRect.x} / span ${previewRect.width}`,
									gridRow: `${previewRect.y} / span ${previewRect.height}`,
								}}
							/>
						) : null}

						{widgets.map((widget, index) => {
							const x = Number(widget?.xCoordinate) || 1;
							const y = Number(widget?.yCoordinate) || 1;
							const width = Number(widget?.width) || 1;
							const height = Number(widget?.height) || 1;

							return (
								<div
									key={`${widget.id || 'widget'}-${index}`}
									className="relative p-1"
									style={{
										gridColumn: `${x} / span ${width}`,
										gridRow: `${y} / span ${height}`,
									}}
								>
									{isEditMode ? (
										<div
											onMouseDown={(event) => startMove(event, index)}
											className="flex h-full w-full cursor-move items-center justify-center rounded-xl border border-slate-500 bg-slate-700 px-3 text-center text-sm font-semibold tracking-wide text-slate-100"
										>
											{getWidgetName(widget)}
										</div>
									) : (
										<ReportWidget
											widget={widget}
											filterValues={filterValues}
											onFilterValueChange={handleFilterValueChange}
											onWidgetPatch={(patch) => handleWidgetPatch(index, patch)}
										/>
									)}
									{isEditMode ? (
										<>
											<button
												type="button"
												onMouseDown={(event) => startResize(event, index, 'left')}
												className="absolute left-0 top-1/2 z-[11] h-8 w-2 -translate-y-1/2 rounded-r bg-slate-300/80 opacity-70 transition hover:bg-slate-200 hover:opacity-100 cursor-ew-resize"
												aria-label="Resize left"
												title="Resize left"
											/>
											<button
												type="button"
												onMouseDown={(event) => startResize(event, index, 'right')}
												className="absolute right-0 top-1/2 z-[11] h-8 w-2 -translate-y-1/2 rounded-l bg-slate-300/80 opacity-70 transition hover:bg-slate-200 hover:opacity-100 cursor-ew-resize"
												aria-label="Resize right"
												title="Resize right"
											/>
											<button
												type="button"
												onMouseDown={(event) => startResize(event, index, 'top')}
												className="absolute left-1/2 top-0 z-[11] h-2 w-8 -translate-x-1/2 rounded-b bg-slate-300/80 opacity-70 transition hover:bg-slate-200 hover:opacity-100 cursor-ns-resize"
												aria-label="Resize top"
												title="Resize top"
											/>
											<button
												type="button"
												onMouseDown={(event) => startResize(event, index, 'bottom')}
												className="absolute bottom-0 left-1/2 z-[11] h-2 w-8 -translate-x-1/2 rounded-t bg-slate-300/80 opacity-70 transition hover:bg-slate-200 hover:opacity-100 cursor-ns-resize"
												aria-label="Resize bottom"
												title="Resize bottom"
											/>
										</>
									) : null}
									{isEditMode ? (
										<div className="absolute right-2 top-2 z-[12] flex items-center gap-1">
											<button
												type="button"
												onClick={() => requestDeleteWidget(index)}
												className="inline-flex h-7 w-7 items-center justify-center rounded-md border border-red-300 bg-white/95 text-red-600 shadow-sm transition hover:bg-red-50"
												aria-label="Delete widget"
												title="Delete widget"
											>
												<svg viewBox="0 0 24 24" fill="none" aria-hidden="true" className="h-4 w-4">
													<path d="M5 7H19" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
													<path d="M10 4H14" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
													<path d="M8 7V18C8 18.6 8.4 19 9 19H15C15.6 19 16 18.6 16 18V7" stroke="currentColor" strokeWidth="1.8" strokeLinejoin="round" />
													<path d="M10.5 10V16" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
													<path d="M13.5 10V16" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
												</svg>
											</button>
											<button
												type="button"
												onClick={() => openEditor(index)}
												className="inline-flex h-7 w-7 items-center justify-center rounded-md border border-slate-300 bg-white/95 text-slate-700 shadow-sm transition hover:bg-slate-100"
												aria-label="Edit widget"
												title="Edit widget"
											>
												<svg viewBox="0 0 24 24" fill="none" aria-hidden="true" className="h-4 w-4">
													<path d="M4 20H8L18.2 9.8C18.6 9.4 18.6 8.7 18.2 8.3L15.7 5.8C15.3 5.4 14.6 5.4 14.2 5.8L4 16V20Z" stroke="currentColor" strokeWidth="1.8" strokeLinejoin="round" />
													<path d="M13.5 6.5L17.5 10.5" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
												</svg>
											</button>
										</div>
									) : null}
								</div>
							);
						})}
					</div>
				</div>
			</section>

			{isEditMode && editingWidgetIndex !== null ? (
				<div className="fixed inset-0 z-[200000] flex items-start justify-center overflow-y-auto px-3 pb-4 pt-20">
					<div className="absolute inset-0 bg-black/40" />
					<div className="relative flex h-[min(88vh,calc(100dvh-96px))] w-full max-w-lg min-h-0 flex-col overflow-hidden rounded-xl border border-slate-200 bg-white shadow-2xl">
						<div className="flex items-center justify-between border-b border-slate-200 px-3 py-2">
							<h4 className="text-sm font-semibold tracking-tight text-slate-900">Edit Widget</h4>
							<button
								type="button"
								onClick={closeEditor}
								disabled={isSaving}
								className="inline-flex h-7 w-7 items-center justify-center rounded-md border border-slate-300 text-slate-600 transition hover:bg-slate-100"
								aria-label="Close editor"
								title="Close"
							>
								x
							</button>
						</div>
						<div className="min-h-0 flex-1 overflow-auto p-3">
							<ReportWidgetEdit
								widget={draftWidget || widgets[editingWidgetIndex]}
								onChange={setDraftWidget}
							/>
						</div>
						<div className="flex items-center justify-end gap-2 border-t border-slate-200 px-3 py-2">
							<button
								type="button"
								onClick={closeEditor}
								disabled={isSaving}
								className="rounded-md border border-slate-300 px-3 py-1.5 text-xs font-medium text-slate-700 transition hover:bg-slate-100 disabled:cursor-not-allowed disabled:opacity-60"
							>
								Close
							</button>
							<button
								type="button"
								onClick={handleSaveWidget}
								disabled={isSaving}
								className="rounded-md bg-slate-900 px-3 py-1.5 text-xs font-medium text-white transition hover:bg-slate-800 disabled:cursor-not-allowed disabled:opacity-60"
							>
								{isSaving ? 'Saving...' : 'Save'}
							</button>
						</div>
					</div>
				</div>
			) : null}

			{isEditMode && deleteWidgetIndex !== null ? (
				<div className="fixed inset-0 z-[210000] flex items-center justify-center px-3">
					<div className="absolute inset-0 bg-black/40" />
					<div className="relative w-full max-w-sm rounded-xl border border-slate-200 bg-white shadow-2xl">
						<div className="border-b border-slate-200 px-4 py-3">
							<h4 className="text-sm font-semibold text-slate-900">Delete Widget</h4>
						</div>
						<div className="px-4 py-3 text-sm text-slate-700">
							Are you sure you want to delete this widget?
						</div>
						<div className="flex items-center justify-end gap-2 border-t border-slate-200 px-4 py-3">
							<button
								type="button"
								onClick={cancelDeleteWidget}
								disabled={isSaving}
								className="rounded-md border border-slate-300 px-3 py-1.5 text-xs font-medium text-slate-700 transition hover:bg-slate-100 disabled:cursor-not-allowed disabled:opacity-60"
							>
								Cancel
							</button>
							<button
								type="button"
								onClick={confirmDeleteWidget}
								disabled={isSaving}
								className="rounded-md bg-red-600 px-3 py-1.5 text-xs font-medium text-white transition hover:bg-red-700 disabled:cursor-not-allowed disabled:opacity-60"
							>
								{isSaving ? 'Deleting...' : 'Delete'}
							</button>
						</div>
					</div>
				</div>
			) : null}
		</div>
	);
}

export default ReportGrid;
